import React, { FC, useMemo, useRef } from 'react'
import { useTranslate } from '@ds/comp-private'
import { useTimer, useUniqueId, useHtmlDir, mergeRefs } from '@ds/react-utils'
import { useIsInk, InputDescription, InputForwardRef } from '@ds/ui'
import { logComponentDeprecation } from '@ds/logging'
import { Locale } from '@ds/base'
import type {
  CallingCode,
  CountryCode,
} from '../FormattedTextBox/formatters/createPhoneNumberFormatter'
import {
  formatPhoneNumber,
  parsePhoneNumber,
  PhoneNumber,
} from '../FormattedTextBox/formatters/createPhoneNumberFormatter/parsePhoneNumber'
import { CallingCodeMenu, Country } from './CallingCodeMenu'
import { FreeformCode, FREEFORM_CODE, PhoneInputSize } from './types'
import { FormattedTextBox } from './FormattedTextBox'
import { Extension } from './Extension'
import { Freeform } from './Freeform'
import {
  phoneInputContainer,
  phoneInputWithoutExtension,
  getExtensionContainerStyles,
  extensionErrorContainer,
} from './styles'
import { noop } from 'lodash'

/*

    PhoneInput takes a calling code and national number, and displays a formatted phone number. The user can
    change the calling code in one of two ways. First, they can select it from a searchable list. Second, they
    can enter it into the national number input prefixed with a "+" sign. By allowing direct entry to the input
    field we enable the following behaviors:

      - The user can supply a calling code without opening the menu.
      - The user can paste (or autofill) an entire phone number and the calling code will be absorbed into the
        appropriate element.
      - The consuming application can persist the phone number as a single string, or integrate with other
        systems that do so. They can provide the full phone number without having to do any additional parsing
        on their end.

    The input of a calling code is considered to be in-progress from the time a "+" is typed until the first
    digit of the national number is typed. During that time the would-be calling code is displalyed in the text
    box where the national number would normally appear.

    When the user makes a change, to either the calling code or national number, the phone number is parsed
    and reported back to the application as an object.

*/

export interface FreeformOption {
  /** Text for the freeform option that will be displayed in the drop-down menu. */
  optionText: string
  /** Text that will be displayed upon selecting the freeform option. */
  selectedText: string
}

export interface PhoneInputProps {
  /** The currently selected calling code. */
  callingCode: string
  /** The value to display next to the calling code. */
  nationalNumber: string
  /** Fired whenever the user changes the calling code or national number. */
  onChangeNumber: (phoneNumber: PhoneNumber) => void
  /** Identifier for automated testing. Passed to the input element directly, and used as a prefix for complementary elements. */
  'data-qa'?: string
  /** Custom data-qa values for elements of this component. Overrides the data-qa prop. */
  qa?: {
    nationalNumberInput?: string
    extensionInput?: string
    menuTrigger?: string
    menuContainer?: string
    searchInput?: string
    getMenuItem?: (item: { countryCode: string; callingCode: string }) => string
  }
  /** If an extension is provided a dedicated text field will render next to the rest of the phone number. */
  extension?: string
  /** Fired when the user types into the extension field. Only numbers can be entered. */
  onChangeExtension?: (extenstion: string) => void
  /** When true the user will see a dropdown to aid in calling code selection. */
  useCallingCodeMenu?: boolean
  /** A custom element to replace the built-in calling code menu. Will not be rendered if `useCallingCodeMenu` is `false`. */
  callingCodeMenu?: React.ReactNode
  /** Displayed at the top of the calling code menu. These can be provided as 2-digit ISO country codes (e.g. "US"), country calling codes (e.g. "1"), or a mix of both. */
  preferredCountryCodes?: (CountryCode | CallingCode | FreeformCode)[]
  /**
   * Excludes the provided countries from the built-in calling code menu.
   */
  excludedCountryCodes?: CountryCode[]
  /** Allows the user to go into a freeform mode by selecting the option from the callingCodeMenu. */
  allowFreeform?: boolean
  /** Sets the input into a freeform mode without allowing the user to opt out of it. The callingCodeMenu will not be shown. */
  useFreeform?: boolean
  /** Overrides the default text for the freeform option. */
  freeformOption?: FreeformOption
  /** @deprecated An element to display on the left side of the TextBox. Will be rendered only if `useCallingCodeMenu` is `false`. */
  leftElement?: React.ReactNode
  /** An element to display before the TextBox. Will be rendered only if `useCallingCodeMenu` is `false`. */
  startElement?: React.ReactNode
  /** @deprecated An element to display on the right side of the TextBox. */
  rightElement?: React.ReactNode
  /** An element to display at the end of the TextBox. */
  endElement?: React.ReactNode
  /** Shows a red asterisk next to the "Phone Number" label indicating that the field is required. Extension is never required. */
  required?: boolean
  /** When true the phone input will display a translated error message. */
  showInvalidError?: boolean
  /** Shows a custom error message, overriding showInvalidError (above). */
  error?: string
  /**
   * Although an extension is never required, you may want to perform some validation in case one is entered.
   * This message will be shown as an error relating to the extension field. The `error` and `showInvalidError`
   * props will take precedence when used in combination with `extensionError`.
   */
  extensionError?: string
  /** Hide the TextBox label while allowing assistive devices to identify the control. */
  hideLabel?: boolean
  /** See above for hideLabel. This one applies to the extension field. */
  hideLabelExt?: boolean
  /** Defines the maximum number of characters that can be entered into the phone number field. */
  maxLength?: number
  /** Defines the maximum number of characters that can be entered into the extension field. */
  maxLengthExt?: number
  /** Placeholder text for the input. */
  placeholder?: string
  /** Placeholder text for the extension field. */
  placeholderExt?: string
  /** Instructs the browser to offer suggestions for the phone number and extension fields, based on the user's previous entries. */
  autofill?: boolean
  /** Label for the input. */
  label?: string
  /** Label for the extension input. */
  labelExt?: string
  /** Disables the calling code menu, phone number input, and extension input. */
  disabled?: boolean
  /** Subtext that goes under the TextBox label */
  description?: string
  /** Makes the input readOnly */
  readOnly?: boolean
  /** A React ref to assign to the HTML node representing the text input element of the PhoneInput. */
  forwardedRef?: InputForwardRef
  /** Optional locale to override the default lang on the page */
  locale?: Locale
  /** Size of the input */
  size?: PhoneInputSize
}

/**
 * Renders an input for phone numbers, with optional extension field and calling code menu.
 *
 * @param props
 */
export const PhoneInput: FC<PhoneInputProps> = ({
  callingCode,
  nationalNumber,
  onChangeNumber,
  extension,
  onChangeExtension,
  useCallingCodeMenu = true,
  callingCodeMenu,
  preferredCountryCodes,
  excludedCountryCodes,
  leftElement,
  startElement,
  rightElement,
  endElement,
  required = false,
  error,
  extensionError,
  showInvalidError,
  'data-qa': data_qa,
  qa,
  useFreeform = false,
  allowFreeform = false,
  freeformOption,
  hideLabel = false,
  hideLabelExt = false,
  maxLength = 40,
  maxLengthExt = 20,
  placeholder,
  placeholderExt,
  autofill = true,
  label,
  labelExt,
  disabled = false,
  description,
  readOnly,
  forwardedRef,
  locale,
  size = 'medium',
}) => {
  if (leftElement || rightElement) {
    logComponentDeprecation(
      'props "leftElement" and "rightElement" have been deprecated and will be removed in a future release. Please use "startElement" and "endElement" instead.',
      'PhoneInput'
    )
  }

  const inputRef = useRef<HTMLInputElement | null>(null)
  const containerRef = useRef<HTMLInputElement | null>(null)
  const extContainerRef = useRef<HTMLInputElement | null>(null)

  const isInk = useIsInk()
  const translate = useTranslate(locale)
  const textDirection = useHtmlDir()
  const isRTL = textDirection === 'rtl'
  const { runLater: deferFocus } = useTimer()
  const extensionErrorId = useUniqueId('short')

  const freeformSelected = callingCode === FREEFORM_CODE
  const freeformEnabled = useFreeform || freeformSelected

  const showExtension = extension !== undefined
  const showExtensionError =
    showExtension && !error && !showInvalidError && !!extensionError

  const { formattedNational, formattedInternational } = useMemo(() => {
    if (
      freeformSelected ||
      nationalNumber === '' ||
      nationalNumber[0] === '+'
    ) {
      return {
        formattedNational: nationalNumber,
        formattedInternational: nationalNumber,
      }
    }
    const _formattedNational = formatPhoneNumber(nationalNumber, callingCode)
    return {
      formattedNational: _formattedNational,
      formattedInternational: `+${callingCode} ${_formattedNational}`,
    }
  }, [callingCode, freeformSelected, nationalNumber])

  const handleChangeNationalNumber = (_nationalNumber: string) => {
    onChangeNumber(parsePhoneNumber(_nationalNumber, callingCode))
  }

  const handleChangeCountry = (country: Country) => {
    deferFocus(() => inputRef.current?.focus())
    if (country.countryCode === FREEFORM_CODE) {
      const phoneNumber = parsePhoneNumber(nationalNumber)
      onChangeNumber({
        ...phoneNumber,
        callingCode: FREEFORM_CODE,
      })
    } else {
      onChangeNumber(parsePhoneNumber(nationalNumber, country.callingCode))
    }
  }

  const callingCodeMenuEnabled = !useFreeform && useCallingCodeMenu

  const handleChangeWithoutMenu = (value: string) => {
    let internationalNumber = value
    // Deleting the '+' not allowed unless it's the only chracter remaining
    if (value.length > 1 && value[0] !== '+') {
      internationalNumber = '+' + internationalNumber
    }
    const phoneNumber = parsePhoneNumber(internationalNumber, callingCode)

    onChangeNumber(phoneNumber)

    if (phoneNumber.internationalNumber) {
      // Parsing may add some characters. Say the defaultCallingCode is '1',
      // the input is empty, and the user enters '2'. The parser would add "+1" to the
      // beginning, resulting in "+12". The "+1" is implied but not expressed directly.
      const impliedCharCount =
        phoneNumber.internationalNumber.length - internationalNumber.length
      if (impliedCharCount > 0) {
        return impliedCharCount
      }
    }
    return 0
  }

  const handleChangeFreeformNumber = (_nationalNumber: string) => {
    const phoneNumber = parsePhoneNumber(_nationalNumber)
    onChangeNumber({
      ...phoneNumber,
      callingCode: FREEFORM_CODE,
      nationalNumber: _nationalNumber,
    })
  }

  const errorMessage = useMemo(() => {
    if (error) {
      return error
    }
    if (showInvalidError) {
      return callingCode === '1'
        ? translate('PHONE_INPUT:INVALID_FORMAT', {
            EXPECTED_PHONE_NUMBER_FORMAT: '(XXX) XXX-XXXX',
          })
        : translate('PHONE_INPUT:INVALID_ERROR')
    }
    return undefined
  }, [callingCode, error, showInvalidError, translate])

  const callingCodeMenuNode = (
    <CallingCodeMenu
      disabled={disabled}
      data-qa={data_qa && data_qa + '-calling-codes'}
      qa={qa}
      value={callingCode}
      freeformSelected={freeformSelected}
      onCountrySelect={handleChangeCountry}
      preferredCountryCodes={preferredCountryCodes}
      excludedCountryCodes={excludedCountryCodes}
      allowFreeform={allowFreeform}
      readOnly={readOnly}
      freeformOption={freeformOption}
      anchorRef={containerRef}
      locale={locale}
      size={size}
    />
  )

  const renderWithMenu = () => {
    const commonProps = {
      containerRef,
      'data-qa': qa?.nationalNumberInput || data_qa,
      type: 'tel',
      label: label || translate('PHONE_INPUT:LABEL'),
      disabled,
      value: formattedNational,
      required,
      error: errorMessage,
      maxLength,
      description,
      readOnly,
      forwardedRef,
      startElement: isRTL ? undefined : callingCodeMenu || callingCodeMenuNode,
      endElement: isRTL ? (
        <>
          {callingCodeMenu || callingCodeMenuNode}
          {endElement || rightElement}
        </>
      ) : (
        endElement || rightElement
      ),
      hideLabel,
      size,
      autoComplete: autofill ? 'tel' : 'off',
      placeholder:
        hideLabel && placeholder === undefined
          ? translate('PHONE_INPUT:LABEL')
          : placeholder,
    } as const
    if (freeformSelected) {
      return (
        <Freeform {...commonProps} onChangeText={handleChangeFreeformNumber} />
      )
    }
    return (
      <FormattedTextBox
        {...commonProps}
        isFormattingChar={isFormattingChar}
        accept={accept}
        size={size}
        onChangeText={handleChangeNationalNumber}
      />
    )
  }

  const renderWithoutMenu = () => {
    const commonProps = {
      forwardedRef: mergeRefs(forwardedRef, inputRef),
      'data-qa': qa?.nationalNumberInput || data_qa,
      type: 'tel',
      label: label || translate('PHONE_INPUT:LABEL'),
      disabled,
      value: formattedInternational,
      required,
      error: errorMessage,
      maxLength,
      startElement: startElement || leftElement,
      endElement: endElement || rightElement,
      hideLabel,
      size,
      placeholder:
        hideLabel && placeholder === undefined
          ? translate('PHONE_INPUT:LABEL')
          : placeholder,
      autoComplete: autofill ? 'tel' : 'off',
      description,
      readOnly,
    } as const

    if (freeformEnabled) {
      return (
        <Freeform {...commonProps} onChangeText={handleChangeFreeformNumber} />
      )
    }
    return (
      <FormattedTextBox
        {...commonProps}
        isFormattingChar={isFormattingChar}
        accept={accept}
        onChangeText={handleChangeWithoutMenu}
      />
    )
  }

  const renderExtension = () => {
    if (!showExtension) {
      return null
    }

    const hasDescription = (description?.length ?? 0) > 0
    // We use a non-breaking space (\u00A0) here because the description should only ever
    // be on the primary input. Without this, it would cause the side-by-side inputs to be offset.
    const descriptionText = hasDescription ? '\u00A0' : undefined

    return (
      <div
        css={getExtensionContainerStyles({
          hideLabel,
          hideLabelExt,
          isInk,
        })}
        ref={extContainerRef}
      >
        <Extension
          data-qa={qa?.extensionInput || (data_qa && data_qa + '-extension')}
          aria-describedby={showExtensionError ? extensionErrorId : undefined}
          label={labelExt || translate('PHONE_INPUT:EXTENSION_ABBREVIATED')}
          disabled={disabled}
          readOnly={readOnly}
          value={extension}
          onChangeText={onChangeExtension || noop}
          hideLabel={hideLabel || hideLabelExt}
          description={descriptionText}
          size={size}
          placeholder={
            (hideLabel || hideLabelExt) && placeholderExt === undefined
              ? translate('PHONE_INPUT:EXTENSION_ABBREVIATED')
              : placeholderExt
          }
          autofill={autofill}
          maxLength={maxLengthExt}
          invalid={showExtensionError}
        />
      </div>
    )
  }

  return (
    <div>
      <div css={phoneInputContainer}>
        <div
          css={phoneInputWithoutExtension(
            isRTL,
            useCallingCodeMenu,
            !!startElement,
            !!leftElement
          )}
        >
          {callingCodeMenuEnabled ? renderWithMenu() : renderWithoutMenu()}
        </div>
        {renderExtension()}
      </div>
      {showExtensionError && (
        <div css={extensionErrorContainer}>
          <InputDescription id={extensionErrorId} kind="error">
            {extensionError as string}
          </InputDescription>
        </div>
      )}
    </div>
  )
}

export const isFormattingChar = (character: string, index: number) => {
  if (character === '+' && index === 0) {
    return false
  }
  if (/\d/.test(character)) {
    return false
  }
  return true
}

export const accept = (
  character: string,
  { selectionStart }: { selectionStart: number | null }
) => {
  if (/\d/.test(character)) {
    return true
  }
  if (/[ )(+-]/.test(character)) {
    return true
  }
  if (selectionStart === 0 && character === '+') {
    return true
  }
  return false
}
